import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import { PipelineInterface } from '@interfaces/Pipeline.interface';
import { PipelineLeaderboardResponse } from '@interfaces/PipelineLeaderboard.interface';
import { PipelineOverviewResponse } from '@interfaces/PipelineOverview.interface';
import { PipelineStageInterface } from '@interfaces/PipelineStage.interface';
import { TargetTrackingData } from '@interfaces/TargetTracking.interface';
import { PipelineStatus } from '../../constants/pipeline';
import {
  AutoCompleteItemResolver,
  AutoCompleteResolver
} from '@ui/AutoCompleteField/types';
import type ApiClient from '../ApiClient';
import {
  PipelineStageTask,
  PipelineStageTaskPaginatedResponse
} from '@/interfaces/PipelineStageTask.interface';
import { MissionInterface } from '@/interfaces/Missions.interface';
import type { BulkImport } from '@/interfaces/BulkImport.interface';
import type { MyWorkstreamInterface } from './my-workstreams';
import type { PipelineLandingResponse } from '@/interfaces/PipelineLanding.interface';

export type { MyWorkstreamInterface, MyWorkstreamKind } from './my-workstreams';

/** `filter[key]=value` query params, skipping empty values. Arrays are comma-joined. */
const toFilterParams = (filters?: Record<string, unknown>): string[] =>
  Object.entries(filters ?? {})
    .filter(([, v]) => v !== '' && v !== undefined && v !== null)
    .map(
      ([k, v]) =>
        `filter[${encodeURIComponent(k)}]=${encodeURIComponent(String(v))}`
    );

export interface PipelineCreateUpdatePayload {
  name: string;
  status: number;
  pipeline_type_id?: number;
  is_default: number;
  target_revenue?: string | null | undefined;
  target_date?: string | null | undefined;
  field_group_template_name: string | null;
  business_unit_id?: number | null | undefined;
  business_unit_grouping?: string | null | undefined;
  fields: {
    _tempId?: string;
    id?: number;
    label: string;
    description: string | null;
    order: number;
    field_type_id?: number;
    field_attribute_id?: number;
    options: string[];
    task_ids?: number[];
  }[];
}

export type DealImportPayload = {
  document: File;
  pipeline_stage_id: number;
  fields: DealImportFieldPayload[];
  field_group_template_name?: string;
  do_not_import_columns?: string[];
  skip_rows?: number[];
  selected_matches?: DealImportSelectedMatch[];
};

export type TaskImportPayload = {
  document: File;
  pipeline_stage_id: number;
  fields: DealImportFieldPayload[];
  field_group_template_name?: string;
  do_not_import_columns?: string[];
  skip_rows?: number[];
  skip_assignee_rows?: number[];
  validate_only?: boolean;
};

export type DealImportDuplicateGroup = {
  row: number;
  deal_name: string;
  type: 'individual' | 'company' | 'team_member';
  value: string;
  options: {
    id: number;
    name: string;
    email: string | null;
    telephone: string | null;
  }[];
};

export type DealImportSelectedMatch = {
  row: number;
  type: 'individual' | 'company' | 'team_member';
  id: number;
};

export type DealImportFieldPayload = {
  id?: number;
  label: string;
  field_type_id?: number;
  field_attribute_id?: number;
  order?: number;
};

export type DealImportResult = {
  imported_count: number;
  failed_count: number;
  valid_count: number;
  skipped_count: number;
  errors: {
    row: number;
    errors: Record<string, string>;
  }[];
  duplicate_groups: DealImportDuplicateGroup[];
};

export type TaskImportResult = Omit<DealImportResult, 'duplicate_groups'> & {
  unmatched_assignees: {
    row: number;
    task_name: string;
    assigned_team_member_email: string;
  }[];
};

/**
 * Board columns paginate one request per column. At 5 the load-more sentinel
 * is usually already on screen, so each column immediately chains pages 2 and 3.
 * 25 is the API's own default_size (config/json-api-paginate.php) and well under
 * its max_results of 500.
 */
const KANBAN_COLUMN_PAGE_SIZE = 25;

export class ApiPipeline extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }

  async getDealImportFields(pipelineId: number) {
    const response = await this.client.get(
      `/pipelines/${pipelineId}/deals/import-fields`,
      {
        headers: { Version: '2025-11-14' }
      }
    );

    return response.data.data;
  }

  async getTaskImportFields(pipelineId: number) {
    const response = await this.client.get(
      `/pipelines/${pipelineId}/tasks/import-fields`,
      {
        headers: { Version: '2025-11-14' }
      }
    );

    return response.data.data;
  }

  async importTasks(
    pipelineId: number,
    payload: TaskImportPayload
  ): Promise<BulkImport<TaskImportResult> | TaskImportResult> {
    const formData = new FormData();
    formData.append('document', payload.document);
    formData.append('pipeline_stage_id', String(payload.pipeline_stage_id));
    if (payload.field_group_template_name) {
      formData.append(
        'field_group_template_name',
        payload.field_group_template_name
      );
    }
    if (payload.validate_only !== undefined) {
      formData.append('validate_only', payload.validate_only ? '1' : '0');
    }

    payload.fields.forEach((field, index) => {
      if (field.id) {
        formData.append(`fields[${index}][id]`, String(field.id));
      }
      formData.append(`fields[${index}][label]`, field.label);
      if (field.field_type_id) {
        formData.append(
          `fields[${index}][field_type_id]`,
          String(field.field_type_id)
        );
      }
      if (field.field_attribute_id) {
        formData.append(
          `fields[${index}][field_attribute_id]`,
          String(field.field_attribute_id)
        );
      }
      if (field.order) {
        formData.append(`fields[${index}][order]`, String(field.order));
      }
    });
    payload.do_not_import_columns?.forEach((column, index) => {
      formData.append(`do_not_import_columns[${index}]`, column);
    });
    payload.skip_rows?.forEach((row, index) => {
      formData.append(`skip_rows[${index}]`, String(row));
    });
    payload.skip_assignee_rows?.forEach((row, index) => {
      formData.append(`skip_assignee_rows[${index}]`, String(row));
    });

    const response = await this.client.post(
      `/pipelines/${pipelineId}/tasks/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Version: '2025-11-14'
        }
      }
    );

    return response.data;
  }

  async importDeals(
    pipelineId: number,
    payload: DealImportPayload
  ): Promise<BulkImport<DealImportResult> | DealImportResult> {
    const formData = new FormData();
    formData.append('document', payload.document);
    formData.append('pipeline_stage_id', String(payload.pipeline_stage_id));

    payload.fields.forEach((field, index) => {
      if (field.id) {
        formData.append(`fields[${index}][id]`, String(field.id));
      }
      formData.append(`fields[${index}][label]`, field.label);
      if (field.field_type_id) {
        formData.append(
          `fields[${index}][field_type_id]`,
          String(field.field_type_id)
        );
      }
      if (field.field_attribute_id) {
        formData.append(
          `fields[${index}][field_attribute_id]`,
          String(field.field_attribute_id)
        );
      }
      if (field.order) {
        formData.append(`fields[${index}][order]`, String(field.order));
      }
    });
    payload.do_not_import_columns?.forEach((column, index) => {
      formData.append(`do_not_import_columns[${index}]`, column);
    });

    payload.skip_rows?.forEach((row, index) => {
      formData.append(`skip_rows[${index}]`, String(row));
    });
    payload.selected_matches?.forEach((selectedMatch, index) => {
      formData.append(
        `selected_matches[${index}][row]`,
        String(selectedMatch.row)
      );
      formData.append(`selected_matches[${index}][type]`, selectedMatch.type);
      formData.append(
        `selected_matches[${index}][id]`,
        String(selectedMatch.id)
      );
    });
    const response = await this.client.post(
      `/pipelines/${pipelineId}/deals/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Version: '2025-11-14'
        }
      }
    );

    return response.data;
  }

  /**
   * Gets all items
   */
  async all(version?: string): Promise<Array<PipelineInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          '/pipelines?filter[status]=1',
          version
            ? {
                headers: {
                  Version: version
                }
              }
            : {}
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Gets active pipeline options for dropdowns.
   */
  async options(
    version?: string,
    params?: {
      businessUnitId?: number;
      pipelineType?: 'REVENUE';
    }
  ): Promise<Array<PipelineInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/pipelines/options', {
          params: {
            business_unit_id: params?.businessUnitId,
            pipeline_type: params?.pipelineType
          },
          ...(version
            ? {
                headers: {
                  Version: version
                }
              }
            : {})
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get list of items
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string,
    version?: string
  ): Promise<ListDataResponse<PipelineInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList(
          '/pipelines',
          listData,
          version
            ? {
                headers: {
                  Version: version
                }
              }
            : {}
        )
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a pipeline
   */
  async get(
    id: number,
    filters?: { team_member_id?: string }
  ): Promise<PipelineInterface> {
    const filterParams =
      filters?.team_member_id != null && filters.team_member_id !== ''
        ? `?filter[team_member_id]=${encodeURIComponent(filters.team_member_id)}`
        : '';
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${id}${filterParams}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Team members with ≥1 task on this pipeline (CRM-446 board filter options).
   */
  async getBoardAssignees(
    id: number
  ): Promise<Array<{ id: number; full_name: string; name?: string; last_name?: string }>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${id}/board-assignees`)
        .then((res) => {
          if (res.data?.data) {
            resolve(res.data.data);
          } else if (Array.isArray(res.data)) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get stages of a pipeline. Pass `signal` to cancel a request that is no
   * longer needed, e.g. when the Board's filters change.
   */
  async getPipelineStages(
    id: number,
    version?: string,
    filters?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Array<PipelineStageInterface>> {
    const filterParams = toFilterParams(filters);
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/pipelines/${id}/pipeline-stages${
            filterParams.length > 0 ? `?${filterParams.join('&')}` : ''
          }`,
          {
            ...(version ? { headers: { Version: version } } : {}),
            ...(signal ? { signal } : {})
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get stages of a pipeline
   */
  async getPipelineStageTasks(
    id: number,
    page: number = 1,
    filters?: Record<string, unknown>
  ): Promise<PipelineStageTaskPaginatedResponse> {
    const filterParams = toFilterParams(filters)
      .map((param) => `&${param}`)
      .join('');
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/pipeline-stages/${id}/tasks?page[number]=${page}&page[size]=${KANBAN_COLUMN_PAGE_SIZE}${filterParams}`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get stages of a pipeline
   */
  async getPipelineTasks(id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${id}/tasks`)
        .then((res) => {
          console.log('test', res.data);
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getMyPipelines(): Promise<PipelineInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/my`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Slim My workstreams nav payload (CRM-434).
   * One call returns revenue pipelines (kind=pipeline) and projects (kind=project).
   */
  async getMyWorkstreams(): Promise<MyWorkstreamInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/my-workstreams`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get contract stages
   */
  async getContractStages(): Promise<Array<PipelineStageInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/contract-stages')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Resolver for autocomplete field
   */
  autoCompleteResolver: AutoCompleteResolver = async (
    searchTerm,
    page,
    perPage
  ) => {
    // show only active pipelines
    const filters = {
      status: PipelineStatus.ACTIVE
    };

    const data = await this.list(
      { perPage, page, filters },
      searchTerm || undefined
    );

    return data.items.map((item) => ({
      value: item.id,
      label: item.name
    }));
  };

  /**
   * Item Resolver for autocomplete field
   */
  autoCompleteItemResolver: AutoCompleteItemResolver = async (value) => {
    const item = await this.get(Number(value));
    return {
      value: item.id,
      label: item.name,
      opt: item
    };
  };

  /**
   * Archive a pipeline collection
   */
  async archive(ids: number[]) {
    return this.client.put('/pipelines/archive', {
      data: ids.map((value: number) => ({ id: value }))
    });
  }

  /**
   * Create a pipeline
   */
  async create(data: PipelineCreateUpdatePayload): Promise<PipelineInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/pipelines', data)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a pipeline
   */
  async update(
    data: PipelineCreateUpdatePayload,
    id: number
  ): Promise<PipelineInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/pipelines/${id}`, data)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create a pipeline Stages
   */
  async addStagesOnPipeline(
    pipelineId: number,
    data: {
      name: string;
      color: string;
      order: number;
      sla_days?: number | null;
    }[]
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/pipelines/${pipelineId}/pipeline-stages`, { data })
        .then((res) => {
          if (res.status === 204) {
            resolve({});
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * Update pipeline stages
   */
  async updatePipelineStages(
    pipelineId: number,
    data: {
      id?: number | string;
      name: string;
      color: string;
      order: number;
      sla_days?: number | null;
      /** PBv2 CRM product workflow ids attached to this stage (CRM-731). */
      workflow_ids?: string[];
    }[]
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/pipelines/${pipelineId}/pipeline-stages`, { data })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updatePipelineStagesOrder(pipelineId: number, pipelines: number[]) {
    return await this.client.put(`/pipelines/${pipelineId}/update-order`, {
      pipeline_stage_id: pipelines.join(',')
    });
  }

  async updatePipelineStage(
    pipelineId: number,
    stageId: number,
    name: string,
    color: string
  ) {
    return await this.client.put(
      `/pipelines/${pipelineId}/pipeline-stages/${stageId}`,
      {
        pipeline_id: pipelineId,
        name,
        color
      }
    );
  }

  /**
   * Get target tracking data for a pipeline
   */
  async getTargetTracking(
    id: number,
    userIds?: number[]
  ): Promise<TargetTrackingData> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${id}/targets`, {
          params: {
            filter: {
              user_ids: userIds?.join(',') || ''
            }
          }
        })
        .then((res) => {
          if (res.data?.data) {
            resolve(res?.data?.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getMyTargetTracking(id: number): Promise<TargetTrackingData> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${id}/my/targets`)
        .then((res) => {
          if (res.data?.data) {
            resolve(res?.data?.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get pipeline missions
   */
  async getPipelineMissions(
    pipelineId: number,
    listData: ListDataParams
  ): Promise<ListDataResponse<MissionInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`/pipelines/${pipelineId}/missions`, listData, {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getDataPoints(
    businessUnitId?: number,
    status?: number
  ): Promise<{
    active_tasks: number;
    conversion_rate: number;
    current_value: number;
    /** CRM-271: sum of per-deal capped weighted contributions (matches pipeline detail formula). */
    weighted_value: number;
    /** Sum of monetary value on Won stage only (revenue pipelines in scope). */
    won_revenue: number;
    target_revenue: number;
    tooltip: string;
  }> {
    return new Promise((resolve, reject) => {
      this.client
        .get('pipelines/data-points', {
          params: {
            business_unit_id: businessUnitId,
            status: status
          },
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async landing(): Promise<PipelineLandingResponse> {
    const res = await this.client.get('/pipelines/landing');
    return {
      groups: res.data?.data ?? [],
      monthWindow: res.data?.meta?.month_window ?? []
    };
  }

  /** Company forecast months for pipeline landing. Missing endpoint returns empty months. */
  async companyForecasts(params: {
    from: string;
    to: string;
  }): Promise<{ months: Array<{ month: string; pipelines?: Array<{ id: number; forecast: number }> }> }> {
    try {
      const res = await this.client.get('forecasts', {
        params: {
          scope: 'company',
          from: params.from,
          to: params.to
        },
        headers: {
          Version: '2025-11-14'
        }
      });
      return { months: res.data?.months ?? [] };
    } catch {
      return { months: [] };
    }
  }

  async getOverview(params?: {
    businessUnitId?: number;
    pipelineIds?: number[];
  }): Promise<PipelineOverviewResponse> {
    return new Promise((resolve, reject) => {
      this.client
        .get('pipelines/overview', {
          params: {
            business_unit_id: params?.businessUnitId,
            pipeline_ids: params?.pipelineIds
          },
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getLeaderboard(
    period: string = 'last-month'
  ): Promise<PipelineLeaderboardResponse> {
    const response = await this.client.get('/pipelines/leaderboard', {
      params: {
        filter: {
          period
        }
      },
      headers: {
        Version: '2025-11-14'
      }
    });

    return response.data.data;
  }
}

export { pipelineQueryKeys } from './query-keys';
export type {
  PipelineListQueryParams,
  PipelineMissionsQueryParams,
  PipelineOptionsQueryParams,
  PipelineLeaderboardQueryParams,
  PipelineOverviewQueryParams,
  PipelineStageTasksQueryParams
} from './query-keys';
export {
  usePipelineAllQuery,
  usePipelineContractStagesQuery,
  usePipelineDataPointsQuery,
  usePipelineListQuery,
  usePipelineMissionsQuery,
  usePipelineMyPipelinesQuery,
  usePipelineMyWorkstreamsQuery,
  usePipelineMyTargetTrackingQuery,
  usePipelineOptionsQuery,
  usePipelineLeaderboardQuery,
  usePipelineOverviewQuery,
  usePipelineQuery,
  usePipelineStageTasksQuery,
  usePipelineStagesQuery,
  usePipelineTargetTrackingQuery,
  usePipelineTasksQuery
} from './use-pipeline-queries';
export {
  usePipelineAddStagesMutation,
  usePipelineArchiveMutation,
  usePipelineCreateMutation,
  usePipelineUpdateMutation,
  usePipelineUpdateStageMutation,
  usePipelineUpdateStagesMutation,
  usePipelineUpdateStagesOrderMutation
} from './use-pipeline-mutations';
export type {
  PipelineAddStagesVariables,
  PipelineUpdateStageVariables,
  PipelineUpdateStagesOrderVariables,
  PipelineUpdateStagesVariables,
  PipelineUpdateVariables
} from './use-pipeline-mutations';
